import { backendClient } from "@/lib/api-client";

export interface User {
  user_id?: number; // Backend numeric user ID
  guid: string; // GUID of the user
  first_name: string; // First name of the user
  surname: string; // Surname of the user
  email: string; // Email of the user
  position?: string | null; // Position of the user
  profile_picture_url?: string | null; // URL of the user's profile picture (optional)
  role_id: number; // Role associated with the user (optional)
}

/**
 * Compose a user's display name from their first name and surname.
 * Returns an empty string when neither part is set.
 */
export const fullName = (
  user?: { first_name?: string | null; surname?: string | null } | null
): string => {
  if (!user) return "";
  return [user.first_name, user.surname]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
};

interface UserProfileUpdate {
  first_name?: string; // First name of the user
  surname?: string; // Surname of the user (empty string clears it)
  position?: string | null; // Position of the user (optional)
}

/**
 * Fetch a user by email or identifier
 * Uses the apiClient to make an authenticated request to the backend API
 */
export const getUser = async (userIdentifier: string): Promise<User | null> => {
  try {
    const response = await backendClient.get(`/user`, {
      params: { user_identifier: userIdentifier }
    });
    
    return response.data;
  } catch (error: any) {
    // Handle 404 specifically to return null instead of throwing
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Fetch the canonical list of selectable user positions.
 * The list is owned by the backend so the dropdown never drifts from validation.
 */
export const getPositions = async (): Promise<string[]> => {
  const response = await backendClient.get('/user/positions');
  return response.data;
};

/**
 * Update an existing user
 * Uses the apiClient to make an authenticated request to the backend API
 */
export const updateUser = async (
  guid: string,
  updates: Partial<UserProfileUpdate>
): Promise<User> => {
  try {
    const response = await backendClient.patch(`/user/${guid}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};
